import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { disableHiddenStandardControls, isStandardFieldHidden } from "../../standard-field-visibility.helper";
import { FormBuilder, FormControl, UntypedFormGroup, ValidatorFn, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment-business-days";
import { FormValidations } from "utils/form-validation";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { ToastService } from "@core/components/toasts/toasts.service";
import { WorkforcePlanModalComponent } from "app/views/pages/workforce-plan/components/workforce-plan-modal/workforce-plan-modal.component";
import { pagesActions } from "app/page-access";
import { AuthService } from "services/auth.service";
import { EmployeeService } from "services/corporate/employee.service";
import { DomainService } from "services/domain.service";
import { ApprovalScopeActionType } from "services/enums/approvalScopeActionType.enum";
import { UnhealthinessType } from "services/enums/UnhealthinessType.enum";
import { WorkforceplanMovementType } from "services/enums/WorkforceplanMovementType.enum";
import { WorkforceplanStatusType } from "services/enums/WorkforceplanStatusType.enum";
import { JobTitleService } from "services/hiring/jobTitle.service";
import { JobTitleModel } from "services/models/hiring/jobTitle.model";
import { OptionModel } from "services/models/model";
import { QueryParams } from "services/models/odata/queryParams";
import { Filter } from "services/models/odata/filter";
import { WorkforcePlanModel, WorkforcePlanTeamEmployeeModel, WorkforcePlanWorkScaleGroupModel } from "services/models/workforceplan";
import { WorkforcePlanDrawerEmployeeService } from "services/workforceplan/workforceplan-employee.service";
import { WorkforceplanService } from "services/workforceplan/workforceplan-main.service";
import { WorkforceplanMovementTypeService } from "services/workforceplan/workforceplan-movement-type.service";
import { WorkforceplanTrainingService } from "services/workforceplan/workforceplan-training.service";
import { formattedPhone, guidEmpty } from "utils/helps";
import {
	DrawerDataInput,
	ISolicitationFormComponent,
	MovementSalaryRange,
	RequestTrainingPayload,
	RequestTrainingValues,
} from "../../form-types";
import { normalizeSalaryValue } from "@core/utils/currency-value.helper";
import {
	buildErpApprovalFieldVisibilityParams,
	canEditErpIntegrationFields as resolveCanEditErpIntegrationFields,
	canShowExecutionDate as resolveCanShowExecutionDate,
	canShowTradeUnionField as resolveCanShowTradeUnionField,
} from "../../erp-approval-field-visibility.helper";

type InternalTrainingLists = {
	jobTitleOptions?: OptionModel[];
	jobTitleGroupOptions?: OptionModel[];
	centerCostsOptions?: OptionModel[];
	tradeUnionsOptions?: OptionModel[];
	movementTypeOptions?: OptionModel[];
	workScaleGroupOptions?: OptionModel[];
	activeEmployees?: WorkforcePlanTeamEmployeeModel[];
	jobTitles?: JobTitleModel[];
};

@Component({
	selector: "app-internal-training-form",
	templateUrl: "./internal-training-form.component.html",
	styleUrls: ["./internal-training-form.component.scss"],
})
export class InternalTrainingFormComponent implements OnChanges, OnDestroy, ISolicitationFormComponent {
	@Input() data: DrawerDataInput | null = null;
	/** Origin of the drawer: 'action-required' when opened from action-required list; null otherwise */
	@Input() source: "action-required" | null = null;
	@Input() hiddenStandardFields: string[] = [];

	/** Emits true when employee is being fetched, false when done (success or error). Parent can show loading in employee block. */
	@Output() employeeLoadingChange = new EventEmitter<boolean>();

	form: UntypedFormGroup;
	lists: InternalTrainingLists = {};

	trainingOptions: OptionModel[] = [];
	unhealthinessOptions = UnhealthinessType.buildListEnum();

	private destroy$ = new Subject<void>();
	private _cancelWorkScaleGroups$ = new Subject<void>();

	private minDate = moment().add(1, "d").format("YYYY-MM-DD");
	private canManageEmploymentContractInformation = false;

	isJobTitleGroupsLoading = false;
	isJobTitlesLoading = false;
	isWorkScaleGroupsLoading = false;

	private _openPositionHasJobTitle = false;
	private _openPositionCostCenterId: string | null = null;
	private _openPositionJobTitleGroupId: string | null = null;
	private _openPositionJobTitleId: string | null = null;

	constructor(
		private _formBuilder: FormBuilder,
		private _modalService: NgbModal,
		private _trainingService: WorkforceplanTrainingService,
		private _movementTypeService: WorkforceplanMovementTypeService,
		private _workforceplanService: WorkforceplanService,
		private _employeeService: EmployeeService,
		private _domainService: DomainService,
		private _jobTitleService: JobTitleService,
		private _drawerEmployeeService: WorkforcePlanDrawerEmployeeService,
		private _authService: AuthService,
		private _toastService: ToastService
	) {
		this.form = this._formBuilder.group({
			note: [null, Validators.required],
			movementType: [WorkforceplanMovementType.TRAINNING.id],
			employeeId: [null, Validators.required],
			jobTitleId: [null, [Validators.required, this.sameTrainingJobTitleValidator]],
			trainingTypeId: [null, Validators.required],
			trainingTypeName: [],
			costCenterId: [null, Validators.required],
			workScaleGroup: [null, Validators.required],
			jobTitleGroupId: [null, Validators.required],
			workforcePlanId: [null],
			startTrainingDate: [],
			endTrainingDate: [],
			isApproved: [],
			newSalary: [],
			tradeUnionId: [],
			tradeUnionName: [],
			changeTypeCode: [],
			hasDangerousness: [false],
			unhealthinessType: [],
			executionDate: [null],
			salaryRange: [null],
		});

		this.canManageEmploymentContractInformation = this._authService.hasActionAllowed(
			pagesActions.workforcePlan.canManageEmploymentContractInformation,
			"/workforceplan",
			true
		);
	}

	public isHidden(fieldKey: string): boolean {
		return isStandardFieldHidden(this.hiddenStandardFields, fieldKey);
	}

	private applyHiddenStandardFields(): void {
		disableHiddenStandardControls(this.form, this.hiddenStandardFields);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["hiddenStandardFields"] && this.form && !changes["data"]?.currentValue) {
			this.applyHiddenStandardFields();
		}

		const dataChanged = !!changes["data"]?.currentValue;
		if (!dataChanged || !this.data) return;
		if (this.data.type !== "REQUEST_TRAINING") return;

		this._companyHasTradeUnions = null;
		this._persistedTradeUnionId = null;
		this._pendingEmployeeTradeUnion = false;

		this.form.patchValue({
			workforcePlanId: this.data.workforcePlanId,
			employeeId: this.data.employee?.id && this.data.employee.id !== guidEmpty ? this.data.employee.id : null,
		});

		if (this.source === "action-required") {
			this.form.patchValue({ workScaleGroup: this.data?.workScaleGroup ?? null });
			this.lists.workScaleGroupOptions = this.data.workScaleGroup
				? [{
					label: `Turno ${this.data.workScaleGroup}`,
					value: this.data.workScaleGroup,
				} as OptionModel]
				: [];
		} else if (this.data.mode === "details") {
			this.lists.workScaleGroupOptions = this._mapWorkScaleGroupsToOptions(this.data.workScaleGroups);
		} else {
			this.lists.workScaleGroupOptions = [];
			this.form.patchValue({
				workScaleGroup: this.data.hasSubType ? this.data?.workScaleGroup ?? null : null,
			});
		}

		this.loadTrainings(false);

		if (this.data.mode === "add") {
			if (this.data.hasSubType) {
				this._loadActiveEmployees();
				this._applyOpenPositionForTraining();
				if (this.data.costCenterId && !this.data.costCenterName) {
					this._resolveOrganicCostCenterLabel(this.data.costCenterId);
				}
			} else {
				this._loadEffectiveCostCentersByCompany();
				this._loadEmployeeTrainingData();
			}
			this._loadTradeUnionsOptions();
		} else {
			this._loadEffectiveCostCentersByCompany();
		}

		if (this.data.mode === "details") {
			this.ensureEmployeeLoaded().finally(() => {
				this._buildFormForDetails();
			});
			return;
		}

		this.applyHiddenStandardFields();
	}

	ngOnDestroy(): void {
		this._cancelWorkScaleGroups$.next();
		this._cancelWorkScaleGroups$.complete();
		this.destroy$.next();
		this.destroy$.complete();
	}

	getForm(): UntypedFormGroup {
		return this.form;
	}

	getPayload(): RequestTrainingPayload {
		const {
			note,
			employeeId,
			jobTitleId,
			movementType,
			trainingTypeId,
			effectiveDate,
			workforcePlanId,
			costCenterId,
			jobTitleGroupId,
			startTrainingDate,
			endTrainingDate,
			workScaleGroup,
			isApproved,
			changeTypeCode,
			hasDangerousness,
			unhealthinessType,
			newSalary,
			tradeUnionId,
			executionDate,
			salaryRange,
		} = this.form.getRawValue() as RequestTrainingValues;

		const resolvedUnhealthinessType =
			unhealthinessType ?? this.data?.solicitationDetails?.payloadData?.unhealthinessType;

		const jobTitleName = this._getJobTitleOption(jobTitleId);
		const jobTitleGroupName = this._getJobTitleGroupOption(jobTitleGroupId);
		const costCenterName = this._getCostCenterTitleOption(costCenterId);
		const trainingTypeName = this._getTrainingNameOption(trainingTypeId);

		return {
			note,
			employeeId,
			movementType,
			effectiveDate,
			workScaleGroup,
			workforcePlanId,
			payload: JSON.stringify({
				jobTitleId,
				jobTitleName,
				jobTitleGroupId,
				jobTitleGroupName,
				costCenterId,
				costCenterName,
				trainingTypeId,
				trainingTypeName,
				startTrainingDate,
				endTrainingDate,
				isApproved,
				changeTypeCode,
				hasDangerousness,
				unhealthinessType: resolvedUnhealthinessType,
				newSalary: normalizeSalaryValue(newSalary),
				...this._tradeUnionPayload(tradeUnionId),
				executionDate,
				salaryRange: salaryRange ?? null,
			}),
		} as RequestTrainingPayload;
	}

	getUpdatePayload(): string {
		const {
			startTrainingDate,
			endTrainingDate,
			isApproved,
			changeTypeCode,
			hasDangerousness,
			unhealthinessType,
			newSalary,
			tradeUnionId,
			executionDate,
			salaryRange,
		} = this.form.getRawValue() as RequestTrainingValues;

		const resolvedUnhealthinessType =
			unhealthinessType ?? this.data?.solicitationDetails?.payloadData?.unhealthinessType;

		return JSON.stringify({
			startTrainingDate,
			endTrainingDate,
			isApproved,
			changeTypeCode,
			hasDangerousness,
			unhealthinessType: resolvedUnhealthinessType,
			newSalary: normalizeSalaryValue(newSalary),
			...this._tradeUnionPayload(tradeUnionId),
			executionDate,
			salaryRange: salaryRange ?? null,
		});
	}

	/** Solicitação criada antes da faixa existir não fica bloqueada na homologação. */
	get isSalaryRangeRequired(): boolean {
		return this.data?.mode !== "details" || !!this.data?.solicitationDetails?.payloadData?.salaryRange;
	}

	/** Com faixa salarial, o salário é o valor da faixa e o campo fica oculto. */
	get hasSalaryRange(): boolean {
		return !!this.form.get("salaryRange")?.value;
	}

	onSalaryRangeSelected(range: MovementSalaryRange | null): void {
		if (!this.canEditFieldToChangeJobTitleTrained) return;

		// Ao limpar a faixa o salário volta a ser digitado; não pode ficar com o valor da faixa anterior.
		const newSalaryCtrl = this.form.get("newSalary");
		newSalaryCtrl?.setValue(range ? range.amount : null);
		newSalaryCtrl?.updateValueAndValidity();
	}

	openTrainingModal(): void {
		const modalRef = this._modalService.open(WorkforcePlanModalComponent, {
			centered: true,
			keyboard: false,
			backdrop: "static",
			size: "lg",
			windowClass: "wfp-modal",
			backdropClass: "wfp-modal-backdrop",
		});
		modalRef.closed
			.pipe(takeUntil(this.destroy$), take(1))
			.subscribe(() => this.loadTrainings(false));
	}

	onStartDateChange(ev: any): void {
		this.minDate = moment(ev.target.value).add(1, "d").format("YYYY-MM-DD");
		this.form.get("endTrainingDate")?.setValidators([FormValidations.minDateValidator(this.minDate)]);
		this.form.get("endTrainingDate")?.updateValueAndValidity();
	}

	onTrainingCenterCostChanged(data: OptionModel | null, originFieldName: string): void {
		const field = this.form.get(originFieldName);
		field?.setValidators(Validators.required);

		if (data) {
			this.isJobTitleGroupsLoading = true;
			field?.setValue(data.value);
			this._listJobTitleGroupsFromWorkforcePlan(data.value, this.data!.employee.companyId);
			this._loadWorkScaleGroupsForCostCenter(data.value, { clearSelection: true });
		} else {
			this.isJobTitleGroupsLoading = false;
			field?.setValue(null);
			this.lists.jobTitleGroupOptions = [];
			this._loadWorkScaleGroupsForCostCenter(null, { clearSelection: true });
		}
	}

	onJobTitleGroupChanged(option: OptionModel | null): void {
		if (!option) {
			this.lists.jobTitleOptions = [];
			this.form.get("jobTitleId")?.setValue(null);
			return;
		}
		this._loadJobTitlesByJobTitleGroup(option.value);
	}

	onJobTitleChanged(option: OptionModel | null): void {
		const field = this.form.get("jobTitleId");
		if (!option) {
			field?.setValue(null);
			return;
		}

		if (this.data?.mode !== "add") {
			field?.setValue(option.value);
			return;
		}

		if (this.warnIfSameTrainingJobTitle(option.value)) {
			field?.setValue(null);
			return;
		}

		field?.setValue(option.value);
	}

	async onActiveEmployeeChanged(employee: WorkforcePlanTeamEmployeeModel): Promise<void> {
		if (!this.data) return;
		this.data.employee = { ...this.data.employee, id: employee.id } as WorkforcePlanTeamEmployeeModel;
		await this._loadEmployeeTrainingData();

		if (this.data.hasSubType) {
			this.form.patchValue({
				employeeId: this.data.employee.id,
				...this._tradeUnionPatchFromEmployee(),
			});
			this.form.get("jobTitleId")?.updateValueAndValidity();
			this.warnIfSameTrainingJobTitle();
			return;
		}

		this.lists.centerCostsOptions = [
			{ label: this.data.employee.costCenterName, value: this.data.employee.centerCostId },
		];
		this.lists.jobTitleGroupOptions = [
			{ label: this.data.employee.jobTitleGroupName, value: this.data.employee.jobTitleGroupId },
		];
		this._loadJobTitlesByJobTitleGroup(this.data.employee.jobTitleGroupId);

		this.form.patchValue({
			costCenterId: this.data.employee.centerCostId,
			jobTitleGroupId: this.data.employee.jobTitleGroupId,
			...this._tradeUnionPatchFromEmployee(),
			employeeId: this.data.employee.id,
			jobTitleId: null,
		});
	}

	get isAdmin(): boolean {
		return this._authService.isAdmin;
	}

	get canEditOptionApprovalTraining(): boolean {
		return (
			this.data?.solicitationDetails?.hasStepActionType(
				ApprovalScopeActionType.SHOW_OPTION_TO_APPROVE_AND_FINISH_TRAINING.value
			)
		);
	}

	get canEditTrainingCompletion(): boolean {
		if (!this.canEditOptionApprovalTraining) {
			return false;
		}

		const gate = this.data?.solicitationDetails?.trainingCompletionGate;
		if (gate?.isActive && !gate.canEdit) {
			return false;
		}

		return true;
	}

	get isTrainingCompletionBlocked(): boolean {
		const gate = this.data?.solicitationDetails?.trainingCompletionGate;
		return !!(gate?.isActive && gate?.canEdit === false);
	}

	get trainingCompletionBlockMessage(): string {
		return this.data?.solicitationDetails?.trainingCompletionGate?.blockMessage ?? "";
	}

	get canEditFieldToStartTraining(): boolean {
		return (
			this.data?.solicitationDetails?.hasStepActionType(
				ApprovalScopeActionType.SHOW_FIELD_TO_INDICATE_START_TRAINING.value
			)
		);
	}

	get canShowFieldToStartTraining(): boolean {
		return this.canEditFieldToStartTraining || !!this.data?.solicitationDetails?.payloadData?.startTrainingDate;
	}

	get canShowFieldToEndTraining(): boolean {
		return this.canEditOptionApprovalTraining || !!this.data?.solicitationDetails?.payloadData?.endTrainingDate;
	}

	get canExecute(): boolean {
		return !!(this.data?.solicitation?.status === "PENDING" && this.data?.solicitationDetails?.permission?.canExecute);
	}

	get canEditFieldToChangeJobTitleTrained(): boolean {
		return (
			this.data?.solicitationDetails?.hasStepActionType(
				ApprovalScopeActionType.SHOW_FIELDS_TO_APPROVE_NEW_JOBTITLE_TRAINED.value
			) && this.canExecute
		);
	}

	get canEditErpIntegrationFields(): boolean {
		return resolveCanEditErpIntegrationFields(this._erpFieldVisibilityParams);
	}

	/** null enquanto não carregou; false quando a empresa não tem sindicato cadastrado. */
	private _companyHasTradeUnions: boolean | null = null;
	/** Sindicato já gravado na solicitação. O id do colaborador não entra nessa exceção. */
	private _persistedTradeUnionId: string | null = null;

	get canShowTradeUnionField(): boolean {
		// Empresa sem sindicato não exige sindicato (mesma regra da API); só mostra se a solicitação já tiver um.
		if (this._companyHasTradeUnions === false && !this._persistedTradeUnionId) return false;
		return resolveCanShowTradeUnionField(this._erpFieldVisibilityParams);
	}

	get canEditFieldToApproveUnhealthiness(): boolean {
		return (
			this.data?.solicitationDetails?.hasStepActionType(
				ApprovalScopeActionType.SHOW_FIELD_TO_APPROVE_UNHEALTHINESS.value
			) && this.canExecute
		);
	}

	get canShowFieldsToChangeJobTitleTrained(): boolean {
		if (this.canEditFieldToChangeJobTitleTrained) return true;
		const payload = this.data?.solicitationDetails?.payloadData;
		return (
			!!(
				payload?.hasDangerousness ||
				payload?.newSalary ||
				payload?.changeTypeCode
			) && this.canManageEmploymentContractInformation
		);
	}

	get canShowUnhealthinessField(): boolean {
		if (this.canShowFieldsToChangeJobTitleTrained || this.canEditFieldToApproveUnhealthiness || this.canEditErpUnhealthinessFields) return true;
		return !!this.data?.solicitationDetails?.payloadData?.unhealthinessType && this.canManageEmploymentContractInformation;
	}

	get canShowDangerousnessField(): boolean {
		return this.canShowFieldsToChangeJobTitleTrained || this.canEditErpUnhealthinessFields;
	}

	private get canEditErpUnhealthinessFields(): boolean {
		return !!(
			this.data?.solicitationDetails?.requiresChangeEmployeeParamsForErp() &&
			this.canExecute
		);
	}

	get canShowExecutionDate(): boolean {
		return resolveCanShowExecutionDate(this._erpFieldVisibilityParams);
	}

	private get _erpFieldVisibilityParams() {
		return buildErpApprovalFieldVisibilityParams(
			this.data?.solicitationDetails,
			this.canExecute,
			this.canManageEmploymentContractInformation,
			this.canEditFieldToChangeJobTitleTrained
		);
	}

	get isJobtitleDisabled(): boolean {
		return !!(this.data?.hasSubType && this._openPositionHasJobTitle);
	}

	getSelectedOptionText(options: OptionModel[] | unknown[] | undefined, value: unknown): string | null {
		if (!options || value == null || (options as unknown[]).length === 0) return null;
		const option = (options as OptionModel[]).find((opt) => opt.value === value || opt.label === value);
		return option?.label ?? null;
	}

	private readonly sameTrainingJobTitleValidator: ValidatorFn = (control) => {
		if (this.data?.mode !== "add") {
			return null;
		}

		return this.isSameTrainingDestinationJobTitle(control.value) ? { sameJobTitle: true } : null;
	};

	private isSameTrainingDestinationJobTitle(destinationJobTitleId?: string | null): boolean {
		const employeeJobTitleId = this.data?.employee?.jobTitleId;
		const selectedJobTitleId = destinationJobTitleId ?? this.form.get("jobTitleId")?.value;
		return !!employeeJobTitleId
			&& employeeJobTitleId !== guidEmpty
			&& !!selectedJobTitleId
			&& selectedJobTitleId !== guidEmpty
			&& selectedJobTitleId === employeeJobTitleId;
	}

	private warnIfSameTrainingJobTitle(destinationJobTitleId?: string | null): boolean {
		if (!this.isSameTrainingDestinationJobTitle(destinationJobTitleId)) {
			return false;
		}

		this._toastService.showWarning(
			"Não é possível criar uma solicitação de treinamento para a mesma função do colaborador."
		);
		return true;
	}

	private _buildFormForDetails(): void {
		const { solicitation, solicitationDetails } = this.data!;
		const pd = solicitationDetails?.payloadData || {};
		this._persistedTradeUnionId = pd.tradeUnionId ?? null;

		this.form.patchValue({
			note: solicitationDetails?.note ?? null,
			movementType: solicitation?.movementTypeData?.id ?? WorkforceplanMovementType.TRAINNING.id,
			workforcePlanId: this.data?.workforcePlanId ?? null,
			employeeId: solicitation?.employeeId ?? null,
			jobTitleId: pd.jobTitleId ?? null,
			workScaleGroup: solicitation?.workScaleGroup ?? this.data?.workScaleGroup ?? null,
			trainingTypeId: pd.trainingTypeId ?? null,
			trainingTypeName: pd.trainingTypeName ?? null,
			costCenterId: pd.costCenterId ?? null,
			jobTitleGroupId: pd.jobTitleGroupId ?? null,
			startTrainingDate: pd.startTrainingDate ? (pd.startTrainingDate as string).substring(0, 10) : null,
			endTrainingDate: pd.endTrainingDate ? (pd.endTrainingDate as string).substring(0, 10) : null,
			executionDate: pd.executionDate ? (pd.executionDate as string).substring(0, 10) : null,
			isApproved: pd.isApproved ?? null,
			newSalary: normalizeSalaryValue(pd.newSalary),
			tradeUnionId: pd.tradeUnionId ?? null,
			tradeUnionName: pd.tradeUnionName ?? null,
			changeTypeCode: pd.changeTypeCode ?? null,
			hasDangerousness: pd.hasDangerousness ?? false,
			unhealthinessType: pd.unhealthinessType ?? null,
			salaryRange: pd.salaryRange ?? null,
		});

		if (!this.data?.solicitationDetails?.permission?.canUpdate) {
			this.form.disable();
		}

		// Seed options for selects in details
		if (pd.costCenterId && pd.costCenterName) {
			this.lists.centerCostsOptions = [{ label: pd.costCenterName, value: pd.costCenterId } as OptionModel];
		}
		if (pd.jobTitleGroupId && pd.jobTitleGroupName) {
			this.lists.jobTitleGroupOptions = [
				{ label: pd.jobTitleGroupName, value: pd.jobTitleGroupId } as OptionModel,
			];
		}
		if (pd.jobTitleId && pd.jobTitleName) {
			this.lists.jobTitleOptions = [{ label: pd.jobTitleName, value: pd.jobTitleId } as OptionModel];
		}
		if (pd.trainingTypeId && pd.trainingTypeName) {
			this.trainingOptions = [{ label: pd.trainingTypeName, value: pd.trainingTypeId } as OptionModel];
		}

		// Enable/validate fields according to workflow action type
		if (this.canEditFieldToStartTraining) {
			const ctrl = this.form.get("startTrainingDate");
			ctrl?.enable();
			ctrl?.setValidators([Validators.required, FormValidations.minDateValidator(this.minDate)]);
			ctrl?.updateValueAndValidity();
		}

		if (this.canEditTrainingCompletion) {
			const startTrainingData = moment(pd.startTrainingDate).add(1, "d").format("YYYY-MM-DD");
			const endCtrl = this.form.get("endTrainingDate");
			endCtrl?.enable();
			endCtrl?.setValidators([Validators.required, FormValidations.minDateValidator(startTrainingData)]);
			endCtrl?.updateValueAndValidity();

			const approvedCtrl = this.form.get("isApproved");
			approvedCtrl?.enable();
			approvedCtrl?.setValidators([Validators.required]);
			approvedCtrl?.updateValueAndValidity();
		} else if (this.canEditOptionApprovalTraining) {
			this.form.get("endTrainingDate")?.disable({ emitEvent: false });
			this.form.get("isApproved")?.disable({ emitEvent: false });
		}

		if (this.canShowFieldsToChangeJobTitleTrained) {
			this._loadTradeUnionsOptions();

			if (pd.tradeUnionId && pd.tradeUnionName) {
				this.lists.tradeUnionsOptions = [
					{ label: pd.tradeUnionName, value: pd.tradeUnionId } as OptionModel,
				];
			}
		}

		if (this.canShowFieldsToChangeJobTitleTrained) {
			this._loadMovementTypes();
		}

		if (this.canShowUnhealthinessField) {
			this.form.patchValue({
				unhealthinessType: pd.unhealthinessType
					? UnhealthinessType.getById(Number(pd.unhealthinessType)).value
					: null,
			});
		}

		this.form.get("salaryRange")?.disable();

		if (this.canEditFieldToChangeJobTitleTrained) {
			this.form.get("salaryRange")?.enable();
			const newSalaryCtrl = this.form.get("newSalary");
			const tradeUnionCtrl = this.form.get("tradeUnionId");
			const changeTypeCtrl = this.form.get("changeTypeCode");
			const hasDangerousnessCtrl = this.form.get("hasDangerousness");
			const executionDateCtrl = this.form.get("executionDate");

			newSalaryCtrl?.enable();
			newSalaryCtrl?.setValidators([Validators.required]);
			tradeUnionCtrl?.enable();
			tradeUnionCtrl?.setValidators([Validators.required]);
			changeTypeCtrl?.enable();
			changeTypeCtrl?.setValidators([Validators.required]);
			hasDangerousnessCtrl?.enable();
			hasDangerousnessCtrl?.setValidators([Validators.required]);
			hasDangerousnessCtrl?.setValue(false);

			executionDateCtrl?.enable();
			executionDateCtrl?.setValidators([Validators.required]);

			this.form.updateValueAndValidity();

			const needsSalaryFromRange =
				!!pd.salaryRange && (newSalaryCtrl?.value == null || newSalaryCtrl?.value === "");
			if (needsSalaryFromRange) {
				newSalaryCtrl?.setValue(pd.salaryRange.amount);
			}
		}

		if (this.canEditFieldToApproveUnhealthiness) {
			const unhealthinessCtrl = this.form.get("unhealthinessType");
			unhealthinessCtrl?.enable();
			unhealthinessCtrl?.setValidators([Validators.required]);
			unhealthinessCtrl?.updateValueAndValidity();
		}

		if (this.canEditErpUnhealthinessFields) {
			const hasDangerousnessCtrl = this.form.get("hasDangerousness");

			hasDangerousnessCtrl?.enable();
			hasDangerousnessCtrl?.setValidators([Validators.required]);
			hasDangerousnessCtrl?.updateValueAndValidity();
		}

		if (this.canShowUnhealthinessField && !this.canEditFieldToApproveUnhealthiness) {
			const unhealthinessCtrl = this.form.get("unhealthinessType");
			unhealthinessCtrl?.disable({ emitEvent: false });
			unhealthinessCtrl?.clearValidators();
			unhealthinessCtrl?.updateValueAndValidity({ emitEvent: false });
		}

		this.applyHiddenStandardFields();
	}

	private loadTrainings(isAptitude?: boolean): void {
		this._trainingService
			.listTrainings()
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(res) => {
					const trainings = res.data.filter((x) =>
						isAptitude !== null
							? x.isAptitude === isAptitude
							: this.data?.hasSubType
								? x.validateHeadcountPlanning
								: true
					);
					this.trainingOptions = trainings.map((training) => training.toOption());

					if (trainings.length === 1 && !this.form.get("trainingTypeId")?.value) {
						this.form.get("trainingTypeId")?.setValue(trainings[0].id);
					}
				},
				() => {
					this._toastService.showError("Não foi possível carregar os tipos de treinamento.");
					this.trainingOptions = [];
				}
			);
	}

	private _loadMovementTypes(): void {
		this._movementTypeService
			.listMovementTypes()
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(res) => {
					this.lists.movementTypeOptions = res.data.map(
						(item) => ({ label: `${item.foreignId} - ${item.name}`, value: item.foreignId } as OptionModel)
					);
				},
				() => {
					this.lists.movementTypeOptions = [];
				}
			);
	}

	private _tradeUnionPayload(tradeUnionId: string | null | undefined): {
		tradeUnionId?: string | null;
		tradeUnionName?: string | null;
	} {
		if (this._companyHasTradeUnions === false) {
			if (!this._persistedTradeUnionId) return {};
			const payloadData = this.data?.solicitationDetails?.payloadData;
			return {
				tradeUnionId: this._persistedTradeUnionId,
				tradeUnionName: payloadData?.tradeUnionName ?? this._getTradeUnionTitleOption(this._persistedTradeUnionId),
			};
		}

		return {
			tradeUnionId: tradeUnionId ?? null,
			tradeUnionName: this._getTradeUnionTitleOption(tradeUnionId),
		};
	}

	private _pendingEmployeeTradeUnion = false;

	private _tradeUnionPatchFromEmployee(): { tradeUnionId?: string | null } {
		if (this._companyHasTradeUnions === false) {
			this._pendingEmployeeTradeUnion = false;
			return { tradeUnionId: null };
		}
		if (this._companyHasTradeUnions === true) {
			this._pendingEmployeeTradeUnion = false;
			return { tradeUnionId: this.data?.employee?.tradeUnionId ?? null };
		}
		this._pendingEmployeeTradeUnion = true;
		return {};
	}

	private _applyEmployeeTradeUnionDefault(): void {
		if (this._persistedTradeUnionId) return;
		const tradeUnionCtrl = this.form.get("tradeUnionId");
		if (this._hasTradeUnionValue(tradeUnionCtrl?.value) || !this.data?.employee?.tradeUnionId) return;
		tradeUnionCtrl?.setValue(this.data.employee.tradeUnionId);
	}

	private _clearTradeUnionWhenCompanyHasNone(): void {
		const tradeUnionCtrl = this.form.get("tradeUnionId");
		tradeUnionCtrl?.clearValidators();
		if (!this._persistedTradeUnionId) {
			tradeUnionCtrl?.setValue(null, { emitEvent: false });
		}
		tradeUnionCtrl?.updateValueAndValidity({ emitEvent: false });
	}

	private _hasTradeUnionValue(value: unknown): boolean {
		return !!value && !(Array.isArray(value) && value.length === 0);
	}

	private _loadTradeUnionsOptions(): void {
		if (!this.data?.companyId) return;
		const params = {
			select: ["id", "code", "name"],
			filter: Filter.equalToGuid("companyId", this.data.companyId),
		} as QueryParams;
		this._domainService
			.getTradeUnions(params)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(response) => {
					this.lists.tradeUnionsOptions = response.data.map((i) => i.toOption());
					this._companyHasTradeUnions = this.lists.tradeUnionsOptions.length > 0;
					if (this._companyHasTradeUnions) {
						if (this.data?.mode === "add" && this._pendingEmployeeTradeUnion) {
							this._pendingEmployeeTradeUnion = false;
							this._applyEmployeeTradeUnionDefault();
						}
						return;
					}
					this._pendingEmployeeTradeUnion = false;
					this._clearTradeUnionWhenCompanyHasNone();
				},
				() => {
					// Sem a lista de sindicatos, segue a mesma regra de empresa sem sindicato.
					this.lists.tradeUnionsOptions = [];
					this._companyHasTradeUnions = false;
					this._pendingEmployeeTradeUnion = false;
					this._clearTradeUnionWhenCompanyHasNone();
				}
			);
	}

	private _loadEffectiveCostCentersByCompany(): void {
		if (!this.data?.companyId) return;
		this._workforceplanService
			.listEffectiveCostCentersByCompany(this.data.companyId)
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => {
				this.lists.centerCostsOptions = response.data;
			});
	}

	private _listJobTitleGroupsFromWorkforcePlan(costCenterId: string, companyId: string): void {
		this._workforceplanService
			.listJobTitleGroupsFromWorkforcePlan(companyId, costCenterId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(data) => {
					this.lists.jobTitleGroupOptions = data.data;
					this.isJobTitleGroupsLoading = false;
				},
				() => {
					this._toastService.showError("Não foi possível carregar os cargos.");
					this.lists.jobTitleGroupOptions = [];
					this.isJobTitleGroupsLoading = false;
				}
			);
	}

	private _loadWorkScaleGroupsForCostCenter(
		costCenterId: string | null | undefined,
		options?: { clearSelection?: boolean }
	): void {
		const clearSelection = options?.clearSelection === true;
		this._cancelWorkScaleGroups$.next();

		if (!costCenterId) {
			this.isWorkScaleGroupsLoading = false;
			this._setWorkScaleGroupOptions([], true);
			return;
		}

		if (costCenterId === this.data?.costCenterId) {
			this.isWorkScaleGroupsLoading = false;
			this._setWorkScaleGroupOptions(
				this._mapWorkScaleGroupsToOptions(this.data?.workScaleGroups),
				clearSelection
			);
			return;
		}

		const companyId = this.data?.companyId || this.data?.employee?.companyId;
		if (!companyId) {
			this.isWorkScaleGroupsLoading = false;
			this._setWorkScaleGroupOptions([], true);
			this._toastService.showError("Não foi possível carregar os turnos.");
			return;
		}

		this.isWorkScaleGroupsLoading = true;
		this._workforceplanService
			.getWorkforcePlan(companyId, costCenterId, true)
			.pipe(takeUntil(this._cancelWorkScaleGroups$), takeUntil(this.destroy$))
			.subscribe(
				(plans) => {
					const plan = this._resolveDestinationWorkforcePlan(plans || []);
					if (!plan) {
						this._toastService.showError("Não foi possível carregar os turnos.");
						this._setWorkScaleGroupOptions([], true);
					} else {
						this._setWorkScaleGroupOptions(
							this._mapWorkScaleGroupsToOptions(plan.workScaleGroups),
							clearSelection
						);
					}
					this.isWorkScaleGroupsLoading = false;
				},
				() => {
					this._toastService.showError("Não foi possível carregar os turnos.");
					this._setWorkScaleGroupOptions([], true);
					this.isWorkScaleGroupsLoading = false;
				}
			);
	}

	private _resolveDestinationWorkforcePlan(plans: WorkforcePlanModel[]): WorkforcePlanModel | undefined {
		const findByStatus = (status: string) => plans.find((plan) => plan.status === status);
		return (
			findByStatus(WorkforceplanStatusType.Effective.value) ??
			findByStatus(WorkforceplanStatusType.InApproval.value) ??
			findByStatus(WorkforceplanStatusType.Draft.value) ??
			plans[0]
		);
	}

	private _mapWorkScaleGroupsToOptions(
		workScaleGroups: WorkforcePlanWorkScaleGroupModel[] | undefined
	): OptionModel[] {
		return (workScaleGroups || [])
			.filter((group) => !!group.workScaleGroup)
			.map(
				(group) =>
					({
						label: `Turno ${group.workScaleGroup}`,
						value: group.workScaleGroup,
					}) as OptionModel
			);
	}

	private _setWorkScaleGroupOptions(options: OptionModel[], clearSelection: boolean): void {
		this.lists.workScaleGroupOptions = options;
		const current = this.form.get("workScaleGroup")?.value;
		if (clearSelection || (current && !options.some((option) => option.value === current))) {
			this.form.get("workScaleGroup")?.setValue(null);
		}
	}

	private _loadJobTitlesByJobTitleGroup(jobTitleGroupId: string): void {
		if (!this.data?.companyId) return;

		this.isJobTitlesLoading = true;
		this._jobTitleService
			.getJobTitlesByTitleAndGroup(this.data.companyId, jobTitleGroupId)
			.pipe(takeUntil(this.destroy$))
			.subscribe((data) => {
				this._setJobTitleOptions(data);
				this.isJobTitlesLoading = false;
			},
				() => {
					this._toastService.showError("Não foi possível carregar as funções.");
					this.lists.jobTitleOptions = [];
					this.isJobTitlesLoading = false;
				});
	}

	private _loadJobTitlesForOpenPosition(costCenterId: string, jobTitleGroupId: string): void {
		if (!this.data?.companyId) return;

		this.isJobTitlesLoading = true;
		this._jobTitleService
			.listFromEffectiveWorkforcePlan(this.data.companyId, costCenterId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(response) => {
					const jobTitles = (response.body ?? [])
						.map((item) => JobTitleModel.create(item))
						.filter((item) => item.jobTitleGroupId === jobTitleGroupId);
					this._setJobTitleOptions(jobTitles);
					this.isJobTitlesLoading = false;
				},
				() => {
					this._loadJobTitlesByJobTitleGroup(jobTitleGroupId);
				}
			);
	}

	private _setJobTitleOptions(jobTitles: JobTitleModel[]): void {
		this.lists.jobTitleOptions = jobTitles.map(
			(item) => ({ label: `${item.code} - ${item.title}`, value: item.id } as OptionModel)
		);
	}

	private async _loadActiveEmployees(): Promise<void> {
		if (!this.data?.workforcePlanId) return;
		try {
			const data = await this._drawerEmployeeService.getListActiveEmployees(this.data.workforcePlanId).toPromise();
			this.lists.activeEmployees = data;
		} catch {
			this._toastService.showError("Não foi possível carregar a lista de colaboradores.");
			this.lists.activeEmployees = [];
		}
	}

	private _applyOpenPositionForTraining(): void {
		const position = this.data?.employee;
		if (!position) return;

		const costCenterId = this.data.costCenterId ?? position.centerCostId ?? "";
		const costCenterName = this.data.costCenterName ?? position.costCenterName ?? costCenterId;
		const targetJobTitleId =
			position.jobTitleId && position.jobTitleId !== guidEmpty ? position.jobTitleId : null;

		this._openPositionHasJobTitle = !!targetJobTitleId;
		this._openPositionCostCenterId = costCenterId || null;
		this._openPositionJobTitleGroupId = position.jobTitleGroupId || null;
		this._openPositionJobTitleId = targetJobTitleId;

		if (costCenterId) {
			this.lists.centerCostsOptions = [{ label: costCenterName, value: costCenterId }];
		}

		if (position.jobTitleGroupId) {
			this.lists.jobTitleGroupOptions = [
				{ label: position.jobTitleGroupName, value: position.jobTitleGroupId },
			];
		}

		if (targetJobTitleId) {
			this.lists.jobTitleOptions = [
				{ label: position.jobTitleName, value: targetJobTitleId },
			];
		} else if (costCenterId && position.jobTitleGroupId) {
			this._loadJobTitlesForOpenPosition(costCenterId, position.jobTitleGroupId);
		}

		this.form.patchValue({
			costCenterId: costCenterId || null,
			jobTitleGroupId: position.jobTitleGroupId || null,
			jobTitleId: targetJobTitleId,
		});

		this._loadWorkScaleGroupsForCostCenter(costCenterId || null, { clearSelection: false });
	}

	private _resolveOrganicCostCenterLabel(costCenterId: string): void {
		if (!this.data?.companyId) return;

		this._workforceplanService
			.listEffectiveCostCentersByCompany(this.data.companyId)
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => {
				const match = response.data.find((option) => option.value === costCenterId);
				if (!match) return;

				this.data!.costCenterName = match.label;
				this.lists.centerCostsOptions = [match];
			});
	}

	private async _loadEmployeeTrainingData(): Promise<void> {
		// This component avoids refetching employee details in most cases, but keeps parity with previous behavior.
		try {
			if (!this.data?.employee?.id || this.data.employee.id === guidEmpty) return;
			this.employeeLoadingChange.emit(true);
			const employee = Object.assign({}, this.data.employee);
			const { data } = await this._employeeService.get(employee.id, true).toPromise();

			employee.companyId = data.companyId;
			employee.profileImagePath = data.profileImagePath;
			employee.timeShiftId = data.timeShiftId;
			employee.centerCostId = data.costCenterId;
			employee.costCenterName = data.costCenterName;
			employee.tradeUnionId = data.tradeUnionId;
			employee.cellPhoneNumber = formattedPhone(data.cellPhoneNumber);
			employee.jobTitleId = data.jobTitleId;
			employee.jobTitleName = data.jobTitleName;
			employee.registration = data.registration;

			this.data.employee = {
				...employee,
			} as any;
		} catch {
			this._toastService.showError("Não foi possível carregar os dados do colaborador.");
		} finally {
			this.employeeLoadingChange.emit(false);
		}
	}

	private _isEmployeePartial(): boolean {
		const emp = this.data?.employee as any;
		if (!emp || !emp.id || emp.id === guidEmpty) return false;
		// Fields used by drawer employee section tooltip/details
		return !emp.registration || !emp.costCenterName || !emp.jobTitleName;
	}

	private async ensureEmployeeLoaded(): Promise<void> {
		if (!this._isEmployeePartial()) return;
		await this._loadEmployeeTrainingData();
	}

	private _getJobTitleGroupOption(jobTitleGroupId: string): string {
		const opt = this.lists.jobTitleGroupOptions?.find((item) => item.value === jobTitleGroupId);
		return (opt?.label as string) ?? "";
	}

	private _getJobTitleOption(jobTitleId: string): string {
		const opt = this.lists.jobTitleOptions?.find((item) => item.value === jobTitleId);
		if (!opt?.label) return "";
		return (opt.label as string).split(" - ")[1] || (opt.label as string);
	}

	private _getCostCenterTitleOption(costCenterId: string): string {
		const opt = this.lists.centerCostsOptions?.find((item) => item.value === costCenterId);
		if (!opt?.label) return "";
		return (opt.label as string).split(" - ")[1] || (opt.label as string);
	}

	private _getTradeUnionTitleOption(tradeUnionId: string | null | undefined): string | null {
		if (!tradeUnionId || !this.lists.tradeUnionsOptions?.length) return null;
		const opt = this.lists.tradeUnionsOptions.find((item) => item.value === tradeUnionId);
		return (opt?.label as string) ?? null;
	}

	private _getTrainingNameOption(trainingTypeId: string): string | null {
		if (!trainingTypeId || !this.trainingOptions?.length) return null;
		const opt = this.trainingOptions.find((item) => item.value === trainingTypeId);
		return (opt?.label as string) ?? null;
	}

	get startTrainingDate(): FormControl {
		return this.form.get("startTrainingDate") as FormControl;
	}

	get endTrainingDate(): FormControl {
		return this.form.get("endTrainingDate") as FormControl;
	}
}

